import { useNavigate, useParams } from "@tanstack/react-router";
import { InfoIcon, SendIcon } from "lucide-react";
import { useTitle } from "react-use";

import { trpc } from "@blis/trpc/react";

import { CommandMenu, CommandMenuProvider } from "@/components/CommandMenu";
import { LoadingPage } from "@/components/LoadingPage";
import { SubHeader } from "@/components/SubHeader";
import { Breadcrumb, Breadcrumbs } from "@/components/ui/breadcrumbs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogBody, DialogTrigger } from "@/components/ui/dialog";
import { Menu, MenuItem, MenuTrigger } from "@/components/ui/menu";
import { Popover } from "@/components/ui/popover";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import {
  useInitializeNavStack,
  useNavLocationState,
} from "@/utils/useNavStack";
import {
  ReportProvider,
  useAccessionReport,
} from "../components/ReportContext";
import { ReportPreview } from "../components/ReportPreview";
import {
  ReportSettingsFlyout,
  ReportSettingsView,
} from "../components/ReportSettings";

export function ReportPreviewPage() {
  const params = useParams({ from: "/_auth/_app/accessions_/$id/report" });
  const id = params.id;

  useTitle(`Report - ${id}`);
  useInitializeNavStack();

  const { data: accession, isLoading } = trpc.accession.get.useQuery({
    id: Number(id),
  });
  const { data: orders, isLoading: ordersLoading } =
    trpc.accession.testOrders.useQuery(Number(params.id));

  const { data: reports, isLoading: reportsLoading } =
    trpc.accession.reports.useQuery(Number(params.id));

  const { href, label } = useNavLocationState();

  if (isLoading || ordersLoading || reportsLoading) {
    return <LoadingPage />;
  }

  if (!accession || !orders) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        Accession not found
      </div>
    );
  }

  const reportsAreDisabled = (orders || []).every(
    (order) => order.testSpec.disableReport,
  );

  const breadcrumbs = (
    <Breadcrumbs>
      <Breadcrumb href={href}>{label}</Breadcrumb>
      <Breadcrumb
        href={{ to: "/accessions/$id", params: { id }, state: { href, label } }}
      >
        {id}
      </Breadcrumb>
      <Breadcrumb>Report</Breadcrumb>
    </Breadcrumbs>
  );

  if (reportsAreDisabled) {
    return (
      <>
        <SubHeader>
          <SubHeader.Left>{breadcrumbs}</SubHeader.Left>
        </SubHeader>
        <div className="h-full w-full flex items-center justify-center">
          <Text size="lg" className="text-muted-foreground">
            Reports are disabled for all tests
          </Text>
        </div>
      </>
    );
  }

  return (
    <>
      <ReportProvider
        accession={accession}
        testOrders={orders || []}
        reports={reports || []}
      >
        <SubHeader>
          <SubHeader.Left>{breadcrumbs}</SubHeader.Left>
          <SubHeader.Right>
            <SendButton />
          </SubHeader.Right>
        </SubHeader>
        <div className="flex-1 flex flex-col lg:flex-row overflow-auto">
          <div className="flex flex-col items-center flex-1 w-full">
            <div className="flex flex-col items-center flex-1 p-5 w-full">
              <ReportPreview />
            </div>
          </div>
          <div className="hidden lg:block w-sm p-6 border-l h-full overflow-auto">
            <ReportSettingsView />
          </div>
        </div>
      </ReportProvider>
      <CommandMenuProvider>
        <CommandMenu />
      </CommandMenuProvider>
    </>
  );
}

function SendButton() {
  const navigate = useNavigate();
  const { accession, reason, status, displayedTests, comment } =
    useAccessionReport();

  const generate = trpc.reports.generate.useMutation({
    onSuccess(data) {
      const prefix = data.isPrelim ? "Preliminary" : "Final";
      const suffix = data.preventNotify ? " (notification disabled)" : "";
      toast.success(`${prefix} report generated${suffix}`);
      navigate({ to: "/accessions/$id", params: { id: accession.id } });
    },
    onError(err) {
      toast.error("Error generating report", {
        description: err.message,
      });
    },
  });

  const generateCorrected = trpc.reports.generateCorrected.useMutation({
    onSuccess() {
      toast.success("Corrected report generated");
      navigate({ to: "/accessions/$id", params: { id: accession.id } });
    },
    onError(err) {
      toast.error("Error generating corrected report", {
        description: err.message,
      });
    },
  });

  const generateReport = (testOrders?: number[], preventNotify = false) => {
    if (status === "corrected") {
      if (reason) {
        generateCorrected.mutate({
          accessionId: accession.id,
          reason,
          testOrders,
          comment,
          preventNotify,
        });
      } else {
        toast.error("Must enter a reason for corrected report.");
      }
    } else {
      generate.mutate({
        accessionId: accession.id,
        testOrders,
        status,
        comment,
        preventNotify,
      });
    }
  };

  const isButtonDisabled =
    (status === "corrected" && !reason) ||
    displayedTests.length === 0 ||
    generate.isPending ||
    generateCorrected.isPending;

  return (
    <>
      <div className="flex items-center gap-2 md:hidden">
        <ReportSettingsFlyout />
        <MenuTrigger>
          <Button
            size="icon-sm"
            variant="outline"
            // isLoading={generateCorrected.isPending || generate.isPending}
            isDisabled={isButtonDisabled}
          >
            <SendIcon />
          </Button>
          <Menu aria-label="Send report">
            <MenuItem onAction={() => generateReport(displayedTests, true)}>
              Generate
            </MenuItem>
            <MenuItem onAction={() => generateReport(displayedTests)}>
              <SendIcon data-slot="icon" />
              Generate and send
            </MenuItem>
          </Menu>
        </MenuTrigger>
      </div>
      <div className="hidden md:flex items-center gap-2">
        <PrelimAlert />
        <ReportSettingsFlyout />

        <TooltipTrigger>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onPress={() => generateReport(displayedTests, true)}
            isDisabled={isButtonDisabled}
          >
            Generate
          </Button>
          <Tooltip>Generate report but don't send</Tooltip>
        </TooltipTrigger>
        <TooltipTrigger>
          <Button
            size="sm"
            onPress={() => generateReport(displayedTests)}
            isDisabled={isButtonDisabled}
          >
            <SendIcon />
            Generate and send
          </Button>
          <Tooltip>Generate report and send notifications</Tooltip>
        </TooltipTrigger>
      </div>
    </>
  );
}

function PrelimAlert() {
  const { status, accession } = useAccessionReport();

  if (status !== "preliminary") {
    return null;
  }

  const hasHold = accession.hasHold;
  const hasPending = accession.status !== "completed";

  return (
    <div className="hidden md:flex items-center gap-1">
      <DialogTrigger>
        <Button size="sm" variant="outline">
          <InfoIcon />
          Preliminary
        </Button>
        <Popover>
          <Dialog hideCloseButton>
            <DialogBody>
              <ul className="list-disc list-inside text-sm">
                {hasHold && <li>Active hold</li>}
                {hasPending && <li>Pending tests</li>}
              </ul>
            </DialogBody>
          </Dialog>
        </Popover>
      </DialogTrigger>
    </div>
  );
}
